import { CartUpdateEvent } from '@theme/events';

/**
 * Module-scoped cache for recommendation results, keyed by seed product id.
 *
 * This deliberately lives OUTSIDE the class (module scope, not an instance
 * field) because the cart section is re-rendered via a full HTML morph on
 * every cart change (quantity tweaks, unrelated line items, adds from this
 * carousel itself, etc). That morph tears down and recreates the
 * <cart-upsell-carousel> element each time, which would otherwise reset all
 * per-instance state and force a fresh network fetch + skeleton-loading
 * flash even when the seed product hasn't actually changed. A module-level
 * cache survives element recreation, so repeat views of the same seed
 * render instantly from cache instead of refetching.
 */
const RECOMMENDATIONS_CACHE_TTL_MS = 5 * 60 * 1000;
const recommendationsCache = new Map();

/**
 * A custom element that shows product recommendations seeded from the most
 * recently added cart item, refetching whenever the cart changes.
 */
class CartUpsellCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {string | null} */
  #lastSeed = null;

  /** @type {boolean} */
  #loading = false;

  /** @type {number} */
  #retryCount = 0;

  /** @type {number} */
  #maxRetries = 3;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#track = this.querySelector('.cart-upsell__track');

    const prevButton = this.querySelector('[data-action="prev"]');
    const nextButton = this.querySelector('[data-action="next"]');
    prevButton?.addEventListener('click', () => this.#scroll(-1));
    nextButton?.addEventListener('click', () => this.#scroll(1));

    // Watch for two distinct things that both mean "we need to (re)render":
    //  1. The seed attribute changes (a different product was last added).
    //  2. Our own rendered content gets wiped out from underneath us.
    // The second case happens because the theme's cart section re-render
    // uses DOM morphing rather than a full replace: when the seed product
    // hasn't changed (e.g. bumping the quantity of an item already in the
    // cart, or adding a second unrelated product), the morph can preserve
    // this exact <cart-upsell-carousel> element in place - so
    // connectedCallback never fires again - while still patching its
    // children back to the server-rendered (empty/skeleton) markup, since
    // the server has no idea we'd client-side-rendered real product cards
    // into it. Without watching for that, the seed-based guard below would
    // wrongly conclude "already loaded" and leave the skeleton showing
    // forever.
    this.#observer = new MutationObserver(() => this.#maybeLoad());
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-seed-product-id'],
      childList: true,
      subtree: true,
    });

    // Start loading immediately, regardless of whether the cart drawer is
    // currently open/visible. Fetching doesn't require layout, and kicking
    // it off as soon as the seed is known - right when the item is added -
    // means the recommendations are usually already loaded (or served
    // instantly from the cache above) by the time the shopper actually opens
    // the drawer, instead of them seeing a loading skeleton. This also
    // sidesteps relying on IntersectionObserver, whose callbacks browsers
    // can noticeably delay for backgrounded/inactive tabs.
    this.#maybeLoad();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /**
   * @param {number} direction -1 for previous, 1 for next
   */
  #scroll(direction) {
    if (!this.#track) return;
    const amount = this.#track.clientWidth * 0.8 * direction;
    this.#track.scrollBy({ left: amount, behavior: 'smooth' });
  }

  #maybeLoad() {
    const seed = this.dataset.seedProductId;
    if (!seed || this.#loading) return;

    // Only skip if we're already showing real cards for this exact seed.
    // Comparing seed alone isn't enough - see the comment in
    // connectedCallback about the morph silently resetting our content.
    const hasCards = !!this.#track?.querySelector('.cart-upsell__card');
    if (seed === this.#lastSeed && hasCards) return;

    if (seed !== this.#lastSeed) this.#retryCount = 0;

    const cached = recommendationsCache.get(seed);
    if (cached && Date.now() - cached.timestamp < RECOMMENDATIONS_CACHE_TTL_MS) {
      this.#lastSeed = seed;
      this.#retryCount = 0;
      this.#render(cached.products);
      return;
    }

    this.#loading = true;
    this.#loadRecommendations(seed)
      .then(() => {
        this.#retryCount = 0;
      })
      .catch((error) => {
        console.error('Cart upsell recommendations error:', error);
        this.closest('.cart-upsell')?.setAttribute('hidden', '');

        // Requests made immediately after an add-to-cart can occasionally 404
        // while the storefront session settles. Retry a few times with a short
        // delay before giving up, rather than permanently skipping this seed.
        if (this.#retryCount < this.#maxRetries) {
          this.#retryCount += 1;
          const delay = 600 * this.#retryCount;
          setTimeout(() => {
            if (this.dataset.seedProductId === seed) this.#maybeLoad();
          }, delay);
        } else {
          this.#lastSeed = seed;
        }
      })
      .finally(() => {
        this.#loading = false;
      });
  }

  /** @param {string} seed */
  async #loadRecommendations(seed) {
    const limit = this.dataset.limit || '8';

    const response = await fetch(
      `/recommendations/products.json?product_id=${encodeURIComponent(seed)}&limit=${encodeURIComponent(limit)}&intent=related`
    );
    if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

    const data = await response.json();
    const products = Array.isArray(data.products) ? data.products : [];
    recommendationsCache.set(seed, { products, timestamp: Date.now() });
    // Set lastSeed synchronously, right before the render/DOM mutation it
    // triggers, so the MutationObserver's resulting callback (queued as a
    // microtask) sees consistent state and no-ops instead of re-rendering.
    this.#lastSeed = seed;
    this.#render(products);
  }

  /** @param {Array<Object>} products */
  #render(products) {
    const wrapper = this.closest('.cart-upsell');
    if (!this.#track || !wrapper) return;

    if (!products.length) {
      wrapper.setAttribute('hidden', '');
      return;
    }

    wrapper.removeAttribute('hidden');
    this.#track.innerHTML = products.map((product) => this.#renderCard(product)).join('');

    this.#track.querySelectorAll('[data-add-button]').forEach((button) => {
      button.addEventListener('click', (event) => this.#handleAdd(event));
    });
  }

  /** @param {Object} product */
  #renderCard(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstAvailable = variants.find((variant) => variant.available) || variants[0] || {};
    const image = product.featured_image || (Array.isArray(product.images) ? product.images[0] : '') || '';
    const price = firstAvailable.price;
    const compareAt = firstAvailable.compare_at_price;
    const showCompare = compareAt && Number(compareAt) > Number(price);
    const showDropdown = variants.length > 1;

    const options = variants
      .map(
        (variant) =>
          `<option value="${variant.id}" ${variant.id === firstAvailable.id ? 'selected' : ''} ${
            !variant.available ? 'disabled' : ''
          }>${this.#escape(variant.title)}</option>`
      )
      .join('');

    const variantField = showDropdown
      ? `<select class="cart-upsell__variant-select" data-variant-select aria-label="${this.#escape(
          'Variant'
        )}">${options}</select>`
      : `<input type="hidden" data-variant-select value="${firstAvailable.id || ''}">`;

    return `
      <div class="cart-upsell__card" data-product-id="${product.id}">
        <a class="cart-upsell__image-link" href="${product.url}">
          <img class="cart-upsell__image" src="${this.#imageUrl(image)}" alt="${this.#escape(
      product.title
    )}" loading="lazy" width="260" height="260">
        </a>
        <div class="cart-upsell__info">
          <a class="cart-upsell__title" href="${product.url}">${this.#escape(product.title)}</a>
          <div class="cart-upsell__price-row">
            <span class="cart-upsell__price">${this.#money(price)}</span>
            ${showCompare ? `<span class="cart-upsell__compare-price">${this.#money(compareAt)}</span>` : ''}
          </div>
          ${variantField}
          <button
            type="button"
            class="button cart-upsell__add"
            data-add-button
            data-fallback-variant-id="${firstAvailable.id || ''}"
            ${!firstAvailable.available ? 'disabled' : ''}
          >
            Add
          </button>
        </div>
      </div>
    `;
  }

  /** @param {PointerEvent} event */
  async #handleAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const card = button.closest('.cart-upsell__card');
    const variantField = card?.querySelector('[data-variant-select]');
    const variantId = variantField?.value || button.dataset.fallbackVariantId;
    if (!variantId) return;

    button.disabled = true;
    button.classList.add('cart-upsell__add--loading');

    const cartItemsComponent = document.querySelector('cart-items-component');
    const sectionId = cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;

    try {
      /** @type {Record<string, unknown>} */
      const body = {
        items: [{ id: Number(variantId), quantity: 1 }],
      };

      if (sectionId) {
        body.sections = sectionId;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add product to cart');
      }

      let itemCount;
      if (sectionId && data.sections && data.sections[sectionId]) {
        const parsed = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
        const countElement = parsed.querySelector('[ref="cartItemCount"]');
        itemCount = countElement ? parseInt(countElement.textContent || '0', 10) : undefined;
      }

      document.dispatchEvent(
        new CartUpdateEvent({ ...data, item_count: itemCount }, sectionId || 'cart-upsell-carousel', {
          source: 'cart-upsell-carousel',
          itemCount,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('Cart upsell add error:', error);
    } finally {
      button.disabled = false;
      button.classList.remove('cart-upsell__add--loading');
    }
  }

  /**
   * @param {string} url
   * @param {number} [width]
   */
  #imageUrl(url, width = 300) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
  }

  /**
   * Formats a price. Shopify's storefront /recommendations/products.json
   * endpoint (and most Shopify product JSON endpoints) returns prices as
   * integers in the shop's smallest currency unit (e.g. pence for GBP), not
   * decimal major units, so we divide by 100 before formatting.
   * @param {string | number} amount
   */
  #money(amount) {
    const raw = Number(amount);
    if (Number.isNaN(raw)) return '';
    const value = raw / 100;
    const currency = /** @type {any} */ (window).Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(value);
    } catch {
      return `£${value.toFixed(2)}`;
    }
  }

  /** @param {string} [value] */
  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }
}

if (!customElements.get('cart-upsell-carousel')) {
  customElements.define('cart-upsell-carousel', CartUpsellCarousel);
}
